import { createContext, useState, useMemo } from "react";
import { createTheme } from "@mui/material/styles";

// Non-production deployments (e.g. the "review" branch build) get a warm amber accent
// instead of the usual blue, so the environment is visually obvious at a glance and can't
// be mistaken for production. Set via REACT_APP_DEPLOY_ENV in the deploy workflow.
// Local development counts as a review environment too: without this `npm start` looks and
// behaves exactly like production. Anything that must never reach a deployed environment -
// raw SQL, internal endpoints - keys off isLocalDev instead: review is shown to FAO
// reviewers, so it is not the place for developer internals.
export const isLocalDev =
    typeof window !== "undefined" &&
    ["localhost", "127.0.0.1", "[::1]", "0.0.0.0"].includes(window.location.hostname);

export const isReviewDeploy = process.env.REACT_APP_DEPLOY_ENV === "review" || isLocalDev;

// color design tokens export
export const tokens = (mode) => ({
    ...(mode === "dark"
        ? {
            grey: {
                100: "#272822",
                500: "#1e1f1c",
                700: "#393a37",
                900: "#ffffff"
            },
            primary: isReviewDeploy
                ? {
                    100: "#e0a030",
                    500: "#3a2e1a",
                    700: "#3a2e1a",
                }
                : {
                    100: "#8774e1",
                    500: "#242e48",
                    700: "#242e48",
                },
            fontColor: {
                100: "#ffffff",
            },
        }
        : {
            grey: {
                100: "#ffffff",
                500: "#f3f3f3",
                700: "#E5ECF4",
                900: "#00000099"
            },
            primary: isReviewDeploy
                ? {
                    100: "#C77700",
                    500: "#f0e0c0a8",
                    700: "#e08e00",
                }
                : {
                    100: "#116AAB",
                    500: "#d8ddeda8",
                    700: "#127ecc",
                },
            fontColor: {
                100: "#333333",
            },
        }),
});

// mui theme settings
export const themeSettings = (mode) => {
    const colors = tokens(mode);
    return {
        palette: {
            mode: mode,
            ...(mode === "dark"
                ? {
                    // palette values for dark mode
                    primary: {
                        main: colors.primary[100],
                        light: colors.primary[500],
                        dark: colors.primary[700],
                        fontColor: colors.fontColor[100]
                    },
                    secondary: {
                        main: colors.grey[100],
                        light: colors.grey[500],
                        dark: colors.grey[700],
                    },
                    buttonBase: {
                        main: colors.grey[900]
                    },
                    background: {
                        default: '#121212'
                    }
                }
                : {
                    // palette values for light mode
                    primary: {
                        main: colors.primary[100],
                        light: colors.primary[500],
                        dark: colors.primary[700],
                        fontColor: colors.fontColor[100],
                    },
                    secondary: {
                        main: colors.grey[100],
                        light: colors.grey[500],
                        dark: colors.grey[700],
                    },
                    buttonBase: {
                        main: colors.grey[900]
                    },
                    background: {
                        default: '#f2f2f2'
                    }
                }),
        },
        typography: {
            allVariants: {
                fontFamily: ["Open Sans", "sans-serif"].join(","),
                fontSize: 14,
                lineHeight: 1.66,
            },
            h1: {
                fontWeight: 600,
                lineHeight: 1.2,
            },
            h2: {
                fontWeight: 600,
                fontSize: '15px',
                lineHeight: 1.3,
                '@media (min-width: 1200px)': {
                    fontSize: '17px',
                },
                '@media (min-width: 1520px)': {
                    fontSize: '18px',
                },
            },
            h3: {
                fontSize: 24,
            }
        },
        components: {

            MuiButton: {
                styleOverrides: {
                    root: {
                        fontSize: '13px',
                    }
                },
            },
            MuiAutocomplete: {
                styleOverrides: {
                    root: {
                        '& .MuiOutlinedInput-root': {
                            padding: '3px',
                            '@media (min-width: 1200px)': {
                                padding: '5px',
                            },
                        },
                    },
                    tag: {
                        fontSize: '10px',
                        height: '25px',
                        margin: '0 2px',
                        '@media (min-width: 1200px)': {
                            fontSize: '12px',
                        },
                    },
                    input: {
                        fontSize: '11px',
                        '@media (min-width: 1200px)': {
                            fontSize: '12px',
                        },
                    },
                },
            },
            MuiInputBase: {
                styleOverrides: {
                    root: {
                        padding: '3px',
                        '@media (min-width: 1200px)': {
                            padding: '5px',
                        },
                    },
                    input: {
                        fontSize: '11px',
                        padding: '5px!important',
                        '@media (min-width: 1200px)': {
                            fontSize: '12px',
                        },
                    },
                },
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: {
                        fontSize: '11px',
                        top: '-3px',
                        '@media (min-width: 1200px)': {
                            fontSize: '12px',
                        },
                    },
                },
            },
            MuiSlider: {
                styleOverrides: {
                    markLabel: {
                        fontSize: '8px',
                        marginTop: '-4px',
                        transform: 'rotate(90deg) translateY(8px)',
                        '@media (min-width: 768px)': {
                            transform: 'rotate(0) translateX(-50%)',
                        },
                        '@media (min-width: 1200px)': {
                            fontSize: '13px',
                        },
                    },
                },
            },
            MuiTab: {
                styleOverrides: {
                    root: {
                        fontSize: '13px',
                    },
                },
            },
            MuiChip: {
                styleOverrides: {
                    label: {
                        fontSize: '11px',
                        padding: "0 7px",
                        '@media (min-width: 1200px)': {
                            padding: "0 10px",
                        },
                    },
                    deleteIcon: {
                        padding: '2px'
                    },
                },
            },
            MuiFormControlLabel: {
                styleOverrides: {
                    label: {
                        fontSize: '11px',
                    }
                }
            },
            MuiSelect: {
                styleOverrides: {
                    root: {
                        fontSize: '13px',
                        padding: '10px'
                    },
                },
            },
            MuiMenuItem: {
                styleOverrides: {
                    root: {
                        fontSize: '13px',
                    },
                },
            },
            MuiListItem: {
                styleOverrides: {
                    root: {
                        fontSize: '11px',
                        lineHeight: '15px',
                        padding: '2px 7px 0px 21px',
                        '@media (min-width: 1200px)': {
                            fontSize: '11.6px',
                            lineHeight: '17px',
                            padding: '3px 5px 3px 20px'
                        },
                    },
                },
            },
            MuiLink: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        color: theme.palette.primary.fontColor,
                        cursor: 'pointer',
                        textDecoration: 'underline',
                    }),
                },
            },
        }
    };
};

// context for color mode
export const ColorModeContext = createContext({
    toggleColorMode: () => { },
});

export const useMode = () => {
    const [mode, setMode] = useState("light");

    const colorMode = useMemo(
        () => ({
            toggleColorMode: () =>
                setMode((prev) => (prev === "light" ? "dark" : "light")),
        }),
        []
    );

    const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);
    return [theme, colorMode];
};